/**
 * Redux action types for the Taxonomy Designer module.
 * These are kept in a separate file to avoid bloating the main actionTypes.js.
 */
export const taxonomyActionTypes = {
  // Session lifecycle
  SET_TAXONOMY_SESSION: 'SET_TAXONOMY_SESSION',
  CLEAR_TAXONOMY_SESSION: 'CLEAR_TAXONOMY_SESSION',

  // Chat messages
  SET_TAXONOMY_MESSAGES: 'SET_TAXONOMY_MESSAGES',
  ADD_TAXONOMY_MESSAGE: 'ADD_TAXONOMY_MESSAGE',
  UPDATE_TAXONOMY_MESSAGE: 'UPDATE_TAXONOMY_MESSAGE',

  // Streaming state
  SET_TAXONOMY_STREAMING: 'SET_TAXONOMY_STREAMING',
  SET_TAXONOMY_STREAMING_ERROR: 'SET_TAXONOMY_STREAMING_ERROR',
  /** User stopped the turn — reconcile tools still marked running. */
  CANCEL_TAXONOMY_TOOLS: 'CANCEL_TAXONOMY_TOOLS',

  // Model state (taxonomy tree, quality, versions)
  SET_TAXONOMY_MODEL: 'SET_TAXONOMY_MODEL',
  SET_TAXONOMY_QUALITY: 'SET_TAXONOMY_QUALITY',
  SET_TAXONOMY_VERSIONS: 'SET_TAXONOMY_VERSIONS',
  SET_TAXONOMY_CURRENT_VERSION: 'SET_TAXONOMY_CURRENT_VERSION',

  // Explorer panel state
  SET_TAXONOMY_SELECTED_CATEGORY: 'SET_TAXONOMY_SELECTED_CATEGORY',
  SET_TAXONOMY_SAMPLE_URLS: 'SET_TAXONOMY_SAMPLE_URLS',

  // Sampling info
  SET_TAXONOMY_SAMPLING_INFO: 'SET_TAXONOMY_SAMPLING_INFO',

  // "Your site today" — path groups shown before a taxonomy exists
  SET_TAXONOMY_SITE_SHAPE: 'SET_TAXONOMY_SITE_SHAPE',

  // Saved design (the persisted result of "Save Taxonomy Design")
  SET_TAXONOMY_SAVED_DESIGN: 'SET_TAXONOMY_SAVED_DESIGN',
  SET_TAXONOMY_PENDING_SAVE: 'SET_TAXONOMY_PENDING_SAVE',
  SET_TAXONOMY_SESSION_COST: 'SET_TAXONOMY_SESSION_COST',

  // Undo
  UNDO_TAXONOMY_ACTION: 'UNDO_TAXONOMY_ACTION',

  // Classify Pages lock
  SET_CLASSIFY_PAGES_LOCKED: 'SET_CLASSIFY_PAGES_LOCKED',
};

// ── TypeScript interfaces ──

/**
 * One entry in an agent message's ordered timeline: a run of streamed text,
 * or a row of consecutive tool calls. Tools segments hold ids only — status
 * lives on the TaxonomyToolUse in `toolUses`, so chips flip running→done
 * in place without touching the timeline.
 */
export type TaxonomySegment = { kind: 'text'; text: string } | { kind: 'tools'; toolIds: string[] };

export interface TaxonomyMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  timestamp: number;
  /** Tool use cards embedded in agent messages */
  toolUses?: TaxonomyToolUse[];
  /**
   * Ordered timeline of the agent turn (text interleaved with tool rows), in
   * the order chunks arrived on the stream. Absent on legacy messages —
   * those render as a single chip strip followed by the full text.
   */
  segments?: TaxonomySegment[];
  /** Diff data embedded in agent messages */
  diff?: TaxonomyDiff;
  /** Interactive question for the user to answer (ask_user_question) */
  question?: TaxonomyQuestion;
  /**
   * The "lost the connection" notice. Not an answer — we could not ask
   * whether the turn finished — so a later mount must still reconcile.
   */
  unresolvedTurn?: boolean;
}

export interface TaxonomyToolUse {
  id: string;
  name: string;
  /** User-friendly tool name for display (e.g. "Updating Categories") */
  displayName?: string;
  /**
   * `cancelled` is set client-side when the user stops the turn: the stream
   * is aborted, so no completion chunk ever arrives and a tool would
   * otherwise stay `running` forever — a spinner claiming work that
   * stopped.
   */
  status: 'running' | 'complete' | 'failed' | 'cancelled';
  params?: Record<string, any>;
  result?: Record<string, any>;
  /** FE wall-clock stamp when the running chunk arrived (live elapsed + duration fallback) */
  startedAt?: number;
  /** FE wall-clock stamp when the complete/failed chunk arrived */
  completedAt?: number;
}

export interface TaxonomyCategory {
  name: string;
  pageCount: number;
  children: TaxonomyCategory[];
  /**
   * Hierarchy level: 1 = top-level, 2 = subcategory, 3 = sub-subcategory.
   * May be absent in mock data — default to 1 if missing.
   */
  level?: number;
  /**
   * Sample URLs belonging to this specific node (up to 5).
   * May be absent in mock data — default to [] if missing.
   */
  urls?: string[];
  // Optional GSC metrics (present after "Adding Search Metrics" tool runs)
  total_clicks?: number;
  total_impressions?: number;
  avg_ctr?: number;
  clicks_pct?: number;
  impressions_pct?: number;
}

export interface TaxonomyQuality {
  /**
   * Share of pages with a category, 0-100. OPTIONAL because it is genuinely
   * unknown until something measures it. It used to be coerced to 0 on the
   * way in, which renders as "0% of your pages have a category" — a specific
   * false claim rather than an absence. See HIGH-5 in the engine's
   * docsV3/KNOWN-ISSUES.md.
   */
  coverage?: number;
  overlap: number;
  balanceGini: number;
  largestCategory: number;
  /** Deepest hierarchy level found (1, 2, or 3). Defaults to 1 if absent. */
  depth?: number;
  /** Maximum supported depth (always 3). Defaults to 3 if absent. */
  maxDepth?: number;
  /** Optional letter grade (A, A-, B+, etc.) */
  grade?: string;
  /** Click coverage percentage from traffic-weighted analysis. */
  clickCoverage?: number;
  /** Impression coverage percentage from traffic-weighted analysis. */
  impressionCoverage?: number;
}

/**
 * "Your site today" — the shape of the site as PATHS, before any taxonomy
 * exists. Deliberately a separate type from TaxonomyCategory: these are two
 * different kinds of object, and rendering them in one tree is what produced
 * 4,336 pages on a 2,993-page site (HIGH-5 in the engine's KNOWN-ISSUES).
 */
export interface SiteShapeGroup {
  /** A real URL path, e.g. "/blog" — or "everywhere else" for the tail. */
  path: string;
  pageCount: number;
  /** Present only on the summarised tail: how many groups it stands for. */
  groupCount?: number;
  /**
   * Present on the tail when some of its pages belong to no section at all
   * (engine: total_urls minus every group). The tail's pageCount includes
   * them; the label must not call them "sections".
   */
  unlistedPageCount?: number;
}

export interface SamplingInfo {
  sampled: boolean;
  sample_size: number;
  total_urls: number;
  method: 'fetch_documents' | 'stratified';
  traffic_weighted?: boolean;
}

// ── Interactive question (ask_user_question) ──

export interface TaxonomyQuestionOption {
  label: string;
  description?: string;
}

export interface TaxonomyQuestion {
  id: string;
  /** The full question text displayed to the user */
  question: string;
  /** Short chip/tag label (max ~12 chars), e.g. "Industry", "Approach" */
  header?: string;
  /** Available choices — 2-4 options. An "Other" free-text option is always appended by the UI. */
  options: TaxonomyQuestionOption[];
  /** Allow selecting multiple options (default: false = single select) */
  multiSelect?: boolean;
  /** Set to true once the user has responded */
  answered?: boolean;
  /** Labels of the option(s) the user selected */
  selectedOptions?: string[];
  /** Free-text response if the user chose "Other" */
  customResponse?: string;
}

export interface TaxonomyDiffRow {
  category: string;
  before: number | null;
  after: number | null;
  delta: number;
  type: 'added' | 'removed' | 'unchanged';
}

export interface TaxonomyDiff {
  rows: TaxonomyDiffRow[];
  totalPagesAffected: number;
}

export interface TaxonomyVersion {
  version: string;
  timestamp: number;
  description: string;
  status: 'staging' | 'deployed' | 'archived' | 'saved' | 'draft';
  /**
   * The version this one was made from. The engine's list is every version in
   * the workspace; following these links back from the current version gives
   * one design's own versions (versionLineage). Absent from an engine that
   * does not send it yet.
   */
  parentVersion?: string | null;
}

export interface TaxonomySampleUrl {
  path: string;
  title: string;
  /** L1 category name (e.g. "Products") */
  category?: string;
  /** L2 subcategory name (e.g. "Electronics"), empty if flat */
  subcategory?: string;
  /**
   * L3 leaf name (e.g. "Wireless Headphones"), absent above L3. Without it an
   * L3 node has no field to match its own pages on and renders empty beneath a
   * correct count. Optional: sessions persisted before the backend emitted it
   * simply fall back to the older matching.
   */
  subcategory2?: string;
  /**
   * How many URLs folded into this row: a page plus its `#section` anchors
   * count as one page here (engine#184), and this says how many anchors
   * that was. Absent or 1 when the row is a single URL.
   */
  sections?: number;
  clicks?: number;
  impressions?: number;
}

/**
 * What the running session has cost so far (B.i).
 *
 * Two sources merge into this shape: the stream's per-turn `cost` chunk
 * (running LLM totals, low latency) and the session endpoint's durable
 * per-provider breakdown (survives suspend, includes Zilliz/Snowflake).
 * Every field is optional because either source can arrive first.
 */
export interface TaxonomySessionCost {
  totalUsd?: number;
  inputTokens?: number;
  outputTokens?: number;
  /** Cache tokens are where a resumed session's spend actually is. */
  cacheCreationTokens?: number;
  cacheReadTokens?: number;
  /** Sum over PRICED durable events only — see unpriced counts per provider. */
  meteredUsd?: number;
  providers?: Record<
    string,
    {
      events?: number;
      unpriced_events?: number;
      cost_usd?: number;
      input_tokens?: number;
      output_tokens?: number;
      cache_creation_tokens?: number;
      cache_read_tokens?: number;
      docs?: number;
      requests?: number;
      queries?: number;
      rows?: number;
      duration_ms?: number;
    }
  >;
}

export interface TaxonomySession {
  sessionId: string;
  customerId?: string; // Schema suffix / cust_code (e.g. "KTWS") — identifies the tenant
  status: 'active' | 'deploying' | 'deployed' | 'discarded';
  createdAt: number;
  createdBy: string;
}

/**
 * The result of a successful save. Versions are immutable: saving an existing
 * design name adds a NEW version to it and never replaces the previous one,
 * so `created` distinguishes "new design" from "new version of a design".
 */
export interface TaxonomySavedDesign {
  /**
   * The engine's `taxonomy_design.id`, or null when the response carried none.
   * Nullable on purpose — this record is persisted, so a placeholder id would
   * be indistinguishable from a real one to whoever reads it next.
   */
  designId: number | null;
  name: string;
  /** The version that was just persisted (e.g. "1.3"). */
  version: string;
  /** true = a brand new design; false = a new version of an existing one. */
  created: boolean;
  savedAt: number;
}

/**
 * A save the user has fired and walked away from. The dialog closes the moment
 * the request is sent — a real save materialises centroids server-side and
 * runs for a minute or more, and holding a modal open for that is a spinner,
 * not an interface. The header shows "saving…" from this record, the commit
 * poll looks for the result, and because the record is persisted with the
 * session it survives a refresh: the engine keeps working after the client
 * hangs up (measured committing at 126s after every hop reported failure), so
 * the client must be able to pick the wait back up.
 */
export interface TaxonomyPendingSave {
  name: string;
  sessionId: string;
  /** The draft version being saved, e.g. "1.19" — what the commit will carry. */
  baseVersion: string;
  startedAt: number;
  /**
   * `updatedAt` (epoch ms) of the SAME-NAMED design as of the moment this
   * save fired, read from the designs list before the request went out; null
   * when no such design existed or the baseline read failed. The commit
   * watcher requires a match to be STRICTLY newer than this — existence plus
   * a clock-skew window is not proof when a row with this name was already
   * there, which is the normal shape of the 409 confirm-new-version flow.
   */
  priorUpdatedAt: number | null;
}

export interface TaxonomyState {
  session: TaxonomySession | null;
  messages: TaxonomyMessage[];
  isStreaming: boolean;
  streamingError: string | null;
  model: TaxonomyCategory[];
  quality: TaxonomyQuality | null;
  versions: TaxonomyVersion[];
  /**
   * Null until the engine names a version. It used to default to "v1.0",
   * which is a real version on most tenants: a turn that sent the tenant's
   * version list without a draft version made the header claim the design
   * saved at 1.0 was this session's.
   */
  currentVersion: string | null;
  selectedCategory: string | null;
  sampleUrls: TaxonomySampleUrl[];
  samplingInfo: SamplingInfo | null;
  siteShape: SiteShapeGroup[];
  savedDesign: TaxonomySavedDesign | null;
  pendingSave: TaxonomyPendingSave | null;
  classifyPagesLocked: boolean;
}

export const initialTaxonomyState: TaxonomyState = {
  session: null,
  messages: [],
  isStreaming: false,
  streamingError: null,
  model: [],
  quality: null,
  versions: [],
  currentVersion: null,
  selectedCategory: null,
  sampleUrls: [],
  samplingInfo: null,
  siteShape: [],
  savedDesign: null,
  pendingSave: null,
  classifyPagesLocked: false,
};
